/**
 * Admin Customer Detail API Contract
 *
 * Renamed from visitor-detail.ts — "Customer" aligns with the CUSTOMER role
 * (online/web-registered users), distinct from kiosk walk-up (see kiosk-visitor-detail.ts).
 *
 * Defines the wire format for the BFF aggregation endpoint:
 *   GET /api/v1/admin/customers/:customerId  (ticketing-service)
 *
 * This schema is shared between the ticketing-service (which produces it)
 * and the admin frontend (which consumes it). Both must import from here
 * instead of defining local interfaces.
 */

import { z } from 'zod';

/** Customer profile section — sourced from identity-service */
export const AdminCustomerProfileSchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string(),
  phone: z.string().nullable(),
  registeredAt: z.string(),
  emailVerified: z.boolean(),
});

export type AdminCustomerProfile = z.infer<typeof AdminCustomerProfileSchema>;

/** A single order row in the customer detail view */
export const AdminCustomerOrderSchema = z.object({
  id: z.string(),
  orderNumber: z.string(),
  status: z.string(),
  totalAmount: z.number(),
  createdAt: z.string(),
});

export type AdminCustomerOrder = z.infer<typeof AdminCustomerOrderSchema>;

/** A single ticket row in the customer detail view */
export const AdminCustomerTicketSchema = z.object({
  id: z.string(),
  ticketNumber: z.string(),
  type: z.string(),
  status: z.string(),
  visitDate: z.string(),
});

export type AdminCustomerTicket = z.infer<typeof AdminCustomerTicketSchema>;

/** Gamification summary for the customer — sourced from gamification-service */
export const AdminCustomerGamificationSchema = z.object({
  points: z.number(),
  tier: z.string(),
  badges: z.array(z.string()),
  lifetimePoints: z.number(),
});

export type AdminCustomerGamification = z.infer<typeof AdminCustomerGamificationSchema>;

/** A single wristband row — sourced from wristband-service */
export const AdminCustomerWristbandSchema = z.object({
  wristbandId: z.string(),
  status: z.string(),
  creditBalance: z.number(),
  issuedAt: z.string(),
});

export type AdminCustomerWristband = z.infer<typeof AdminCustomerWristbandSchema>;

/** Top-level response shape for GET /api/v1/admin/customers/:customerId */
export const AdminCustomerDetailResponseSchema = z.object({
  profile: AdminCustomerProfileSchema,
  orders: z.array(AdminCustomerOrderSchema),
  tickets: z.array(AdminCustomerTicketSchema),
  gamification: AdminCustomerGamificationSchema.nullable(),
  wristbands: z.array(AdminCustomerWristbandSchema),
  totalSpent: z.number(),
  visitCount: z.number(),
});

export type AdminCustomerDetailResponse = z.infer<typeof AdminCustomerDetailResponseSchema>;
